import React, { useCallback, useRef, useState } from "react";
import { Alert, Input, Select, Spin } from "antd";
import moment from "moment";
import { getSurveyMonthlySummary, getSurveyTemplatesByFacility, submitSurvey } from "./educationApi";
import OrgFacilityFilter from "./OrgFacilityFilter";
import DynamicSurveyForm from "./SurveyDynamicForm";
import { SURVEY_MONTH_KEY_FORMAT, SURVEY_MONTH_LABEL_FORMAT, getSurveyClientId, getSurveyList, isSurveyOk, loadSurveyTemplate, notifySurveyResult } from "./surveyUtils";

const { Option } = Select;

const labelStyle = { marginBottom: 4, fontSize: 13, color: "#5B6472" };

// Ported from the old app's surveySubmissionForm/index.js — a facility
// manager picks a facility + one of its assigned surveys and submits it.
const SurveySubmission = () => {
  const [orgId, setOrgId] = useState(undefined);
  const [facilityId, setFacilityId] = useState(undefined);
  const [templateId, setTemplateId] = useState(undefined);
  const [respondentName, setRespondentName] = useState("");
  const [respondentType, setRespondentType] = useState("");

  const [assignedTemplates, setAssignedTemplates] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [submissionTemplate, setSubmissionTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [currentMonthSummary, setCurrentMonthSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);

  // guards against a slow response for a previous facility/template landing late
  const selectionRef = useRef({ facilityId: undefined, templateId: undefined });

  const currentMonth = moment().format(SURVEY_MONTH_KEY_FORMAT);
  const currentMonthLabel = moment(currentMonth, SURVEY_MONTH_KEY_FORMAT).format(SURVEY_MONTH_LABEL_FORMAT);

  // Non-blocking "N submissions already recorded this month" hint.
  const refreshMonthlySummary = async (targetFacilityId, targetTemplateId) => {
    if (!targetFacilityId || !targetTemplateId) {
      setCurrentMonthSummary(null);
      return;
    }

    const response = await getSurveyMonthlySummary({ facility_id: targetFacilityId, survey_template_id: targetTemplateId, month: currentMonth, client_id: getSurveyClientId() });
    const selection = selectionRef.current;
    if (selection.facilityId !== targetFacilityId || selection.templateId !== targetTemplateId) return;

    const result = isSurveyOk(response) ? response.result.result : null;
    setCurrentMonthSummary(Array.isArray(result) ? result[0] || null : result || null);
  };

  const resetSurvey = () => {
    setTemplateId(undefined);
    setSubmissionTemplate(null);
    setLoadingTemplate(false);
    setCurrentMonthSummary(null);
  };

  const handleOrgChange = useCallback((value) => {
    selectionRef.current = { facilityId: undefined, templateId: undefined };
    setOrgId(value);
    setFacilityId(undefined);
    setAssignedTemplates([]);
    setLoadingAssigned(false);
    setTemplateId(undefined);
    setSubmissionTemplate(null);
    setLoadingTemplate(false);
    setCurrentMonthSummary(null);
  }, []);

  const handleFacilityChange = async (value) => {
    selectionRef.current = { facilityId: value, templateId: undefined };
    setFacilityId(value);
    resetSurvey();
    setAssignedTemplates([]);

    if (!value) return;

    // Templates a facility manager is allowed to submit for the selected facility.
    setLoadingAssigned(true);
    const response = await getSurveyTemplatesByFacility(value, { client_id: getSurveyClientId() });
    if (selectionRef.current.facilityId !== value) return;
    setAssignedTemplates(getSurveyList(response));
    setLoadingAssigned(false);
  };

  const handleTemplateChange = async (value) => {
    selectionRef.current = { facilityId, templateId: value };
    setTemplateId(value);
    setFormKey((key) => key + 1);
    setSubmissionTemplate(null);

    refreshMonthlySummary(facilityId, value);

    setLoadingTemplate(true);
    const template = await loadSurveyTemplate(value);
    if (selectionRef.current.templateId !== value) return;
    setSubmissionTemplate(template);
    setLoadingTemplate(false);
  };

  const handleSubmit = async (answers) => {
    setSubmitting(true);
    const response = await submitSurvey({
      survey_template_id: templateId,
      facility_id: facilityId,
      client_id: getSurveyClientId(),
      respondent_name: respondentName || "",
      respondent_type: respondentType || "",
      answers,
    });
    setSubmitting(false);

    if (notifySurveyResult(response, "Survey submitted.")) {
      setRespondentName("");
      setRespondentType("");
      setFormKey((key) => key + 1);
      refreshMonthlySummary(facilityId, templateId);
    }
  };

  const submissionCount = currentMonthSummary ? Number(currentMonthSummary.submission_count || 0) : 0;

  return (
    <div>
      {/* which survey: group, facility, survey */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <OrgFacilityFilter orgId={orgId} onOrgChange={handleOrgChange} facilityId={facilityId} onFacilityChange={handleFacilityChange} orgWidth={240} facilityWidth={240} />
        <div>
          <div style={labelStyle}>Survey</div>
          <Select
            showSearch
            optionFilterProp="children"
            placeholder="Select survey"
            style={{ width: 240 }}
            value={templateId}
            onChange={handleTemplateChange}
            loading={loadingAssigned}
            disabled={!facilityId}
          >
            {assignedTemplates.map((template) => {
              const value = template.survey_template_id || template.template_id || template.id;
              return (
                <Option key={value} value={value}>
                  {template.template_name || template.name}
                </Option>
              );
            })}
          </Select>
        </div>
      </div>
      {/* who is answering (optional) */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={labelStyle}>Respondent Name</div>
          <Input placeholder="Optional" style={{ width: 240 }} value={respondentName} onChange={(e) => setRespondentName(e.target.value)} />
        </div>
        <div>
          <div style={labelStyle}>Respondent Type</div>
          <Input placeholder="Optional" style={{ width: 240 }} value={respondentType} onChange={(e) => setRespondentType(e.target.value)} />
        </div>
      </div>

      {facilityId && templateId ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={
            submissionCount > 0
              ? `${submissionCount} submission${submissionCount === 1 ? "" : "s"} already recorded for ${currentMonthLabel}. Submitting again appends to the same monthly report.`
              : `No submissions recorded yet for ${currentMonthLabel}.`
          }
        />
      ) : null}

      <Spin spinning={loadingTemplate}>
        {submissionTemplate ? (
          <DynamicSurveyForm key={`${templateId}-${formKey}`} template={submissionTemplate} submitting={submitting} submitLabel="Submit Survey" onSubmit={handleSubmit} />
        ) : (
          <Alert type="warning" showIcon message="Select a group, facility and survey to begin." />
        )}
      </Spin>
    </div>
  );
};

export default SurveySubmission;
